

import { useState } from "react";
import { selectCurrentMeeting } from "../store/slices/meetingsSlice";
import { useSelector } from "react-redux";
import { useCreateTaskMutation, useDeleteTaskMutation, useGetTasksQuery, useUpdateTaskMutation } from "../api/taskApi";
import { skipToken } from "@reduxjs/toolkit/query";

const TaskTable = () => {
  const currentMeeting = useSelector(selectCurrentMeeting);

  const meetingId = currentMeeting?.id ?? '';
  const familyId = currentMeeting?.family_id ?? '';

  const { data: tasks = [], refetch } = useGetTasksQuery(
    meetingId ? { meetingId } : skipToken
  );

  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  const [taskName, setTaskName] = useState('');
  const [taskDetails, setTaskDetails] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDetails, setEditDetails] = useState('');

  if (!currentMeeting) {
    return <div className="text-center text-red-600">לא נטענה פגישה נוכחית.</div>;
  }

  const handleAddTask = async () => {
    if (!taskName) return;

    try {
      await createTask({
        meeting_id: meetingId,
        family_id: familyId,
        description: taskName,
        comments: taskDetails,
      }).unwrap();

      setTaskName('');
      setTaskDetails('');
      refetch();
    } catch (error) {
      console.error('שגיאה בהוספת משימה:', error);
    }
  };

  const handleCompleteTask = async (index: number) => {
    const task = tasks[index];
    const newStatus = task.completed ? 'בתהליך' : 'הושלם';

    try {
      await updateTask({
        id: task.id,
        meeting_id: meetingId,
        family_id: familyId,
        description: task.name,
        comments: task.details,
        status: newStatus,
      }).unwrap();

      refetch();
    } catch (error) {
      console.error('שגיאה בעדכון משימה:', error);
    }
  };


  
  const handleDeleteTask = async (index: number) => {
    const task = tasks[index];

    try {
      await deleteTask({ id: task.id }).unwrap();
      refetch();
    } catch (error) {
      console.error('שגיאה במחיקת משימה:', error);
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditName(tasks[index].name);
    setEditDetails(tasks[index].details);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditName('');
    setEditDetails('');
  };

  const saveEditing = async (index: number) => {
    const task = tasks[index];

    try {
      await updateTask({
        id: task.id,
        meeting_id: meetingId,
        family_id: familyId,
        description: editName,
        comments: editDetails,
        status: task.completed ? 'הושלם' : 'בתהליך',
      }).unwrap();

      cancelEditing();
      refetch();
    } catch (error) {
      console.error('שגיאה בשמירת עריכה:', error);
    }
  };

  const tasksCount = tasks.length;
  const completedCount = tasks.filter((t) => t.completed).length;

  return (
    <div className="bg-background min-h-screen flex flex-col items-center justify-start py-8 px-4">
      <div className="text-primary text-3xl font-semibold mb-4">ניהול משימות</div>
      <div className="text-gray-600 text-lg mb-6">
        פעולות: <span className="text-danger">{tasksCount}</span> | הושלמו:{' '}
        <span className="text-success">{completedCount}</span>
      </div>

      <div className="w-full max-w-xl bg-white p-8 rounded-2xl shadow-card mb-6">
        <div className="mb-4">
          <label htmlFor="task-name" className="block text-text-main font-medium">
            כותרת המשימה
          </label>
          <input
            type="text"
            id="task-name"
            className="w-full p-3 mt-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="הכנס כותרת"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
          />
        </div>

        <div className="mb-6">
          <label htmlFor="task-details" className="block text-text-main font-medium">
            הערות (אופציונלי)
          </label>
          <textarea
            id="task-details"
            className="w-full p-3 mt-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="הכנס הערות"
            value={taskDetails}
            onChange={(e) => setTaskDetails(e.target.value)}
          ></textarea>
        </div>

        <div className="flex justify-between items-center">
          <button
            className="bg-primary text-white py-2 px-6 rounded-full shadow-button hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={handleAddTask}
          >
            הוסף משימה
          </button>
          <button className="bg-accent text-white py-2 px-6 rounded-full shadow-button hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-accent">
            חזור לעמוד ראשי
          </button>
        </div>
      </div>

      <div className="w-full max-w-xl bg-white p-6 rounded-2xl shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 bg-primary text-white">סטטוס</th>
                <th className="py-2 px-4 bg-primary text-white">הערה</th>
                <th className="py-2 px-4 bg-primary text-white">כותרת</th>
                <th className="py-2 px-4 bg-primary text-white">פעולות</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, index) => (
                <tr key={task.id}>
                  <td className="py-3 px-4 border-t border-border">
                    <button
                      className={`py-1 px-4 rounded-full text-white ${
                        task.completed ? 'bg-success' : 'bg-danger'
                      }`}
                      onClick={() => handleCompleteTask(index)}
                    >
                      {task.completed ? 'הושלמה' : 'פעילה'}
                    </button>
                  </td>

                  <td className="py-3 px-4 border-t border-border">
                    {editingIndex === index ? (
                      <input
                        type="text"
                        className="w-full p-1 border rounded"
                        value={editDetails}
                        onChange={(e) => setEditDetails(e.target.value)}
                      />
                    ) : (
                      task.details
                    )}
                  </td>

                  <td className="py-3 px-4 border-t border-border">
                    {editingIndex === index ? (
                      <input
                        type="text"
                        className="w-full p-1 border rounded"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    ) : (
                      task.name
                    )}
                  </td>

                  <td className="py-3 px-4 border-t border-border space-x-2">
                    {editingIndex === index ? (
                      <>
                        <button
                          className="bg-success text-white py-1 px-4 rounded-full"
                          onClick={() => saveEditing(index)}
                        >
                          שמור
                        </button>
                        <button
                          className="bg-gray-400 text-white py-1 px-4 rounded-full"
                          onClick={cancelEditing}
                        >
                          ביטול
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="bg-primary text-white py-1 px-4 rounded-full"
                          onClick={() => startEditing(index)}
                        >
                          ערוך
                        </button>
                        <button
                          className="bg-danger text-white py-1 px-4 rounded-full"
                          onClick={() => handleDeleteTask(index)}
                        >
                          מחק
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TaskTable;

